// src/Login.js
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios, { API_BASE } from './axiosConfig';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import LanguageSwitcher from './LanguageSwitcher';
import contourImage from './assets/contour_image.png';

const TURNSTILE_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Prefer env-provided site key, fallback to your current hardcoded key
const SITE_KEY =
  (typeof process !== 'undefined' &&
    process.env &&
    process.env.REACT_APP_TURNSTILE_SITE_KEY) ||
  '0x4AAAAAAB2reH6C_-_i4fsM';

const OTP_REQUEST_TIMEOUT_MS = 30_000;
const OTP_VERIFY_TIMEOUT_MS = 20_000;

function loadTurnstileScript() {
  return new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();

    const existing = document.querySelector(`script[src^="${TURNSTILE_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }

    const s = document.createElement('script');
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

function safeSnippet(v, max = 220) {
  try {
    const s = typeof v === 'string' ? v : JSON.stringify(v);
    return (s || '').slice(0, max);
  } catch {
    return '';
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function ensureCsrf() {
  // Only fetch CSRF if cookie is missing.
  // This endpoint should be public and return 204 + Set-Cookie.
  if (!getCookie('csrftoken')) {
    await axios.get('auth/csrf/', { timeout: 10_000 });
  }
}

function diagnoseAxiosError(err, t, isZh) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const msgFromApi =
    data?.email?.[0] ||
    data?.detail ||
    data?.non_field_errors?.[0] ||
    data?.error ||
    (typeof data === 'string' ? data : null);

  const isTimeout =
    err?.code === 'ECONNABORTED' ||
    String(err?.message || '').toLowerCase().includes('timeout');

  const isNetwork =
    !err?.response ||
    String(err?.message || '').toLowerCase().includes('network error') ||
    String(err?.message || '').toLowerCase().includes('failed to fetch');

  if (status === 502 || status === 504) {
    return t('login.diag.serverUnavailable', { status });
  }
  if (status === 403) {
    return t('login.diag.forbidden');
  }
  if (status === 404) {
    return t('login.diag.notFound');
  }
  if (status === 429) {
    return t('login.diag.tooMany');
  }
  if (isTimeout) {
    return t('login.diag.timeout');
  }
  if (isNetwork) {
    return t('login.diag.network');
  }
  if (msgFromApi && !isZh) {
    return `${msgFromApi}${status ? ` (${t('login.diag.httpStatus', { status })})` : ''}`;
  }
  const suffix = status ? ` (${t('login.diag.httpStatus', { status })})` : '';
  const technical = !isZh && safeSnippet(data) ? ` — ${safeSnippet(data)}` : '';
  return `${t('login.diag.requestFailed')}${suffix}${technical}`;
}

export default function Login() {
  const { t, i18n } = useTranslation();
  const isZh = String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh');
  const [step, setStep] = useState('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const [cfToken, setCfToken] = useState('');
  const [widgetId, setWidgetId] = useState(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Helpful runtime diagnostics so you can see “is the API reachable?”
  const [apiStatus, setApiStatus] = useState('checking'); // checking | ok | fail
  const [apiDiag, setApiDiag] = useState('');

  const widgetRef = useRef(null);
  const navigate = useNavigate();

  // 1) Quick backend reachability check (CSRF endpoint is perfect for this)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setApiStatus('checking');
      setApiDiag('');
      try {
        await axios.get('auth/csrf/', { timeout: 12_000 });
        if (!cancelled) {
          setApiStatus('ok');
          setApiDiag('');
        }
      } catch (err) {
        if (!cancelled) {
          setApiStatus('fail');
          setApiDiag(diagnoseAxiosError(err, t, isZh));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [t, isZh]);

  // 2) Turnstile widget
  useEffect(() => {
    let removed = false;
    let id = null;

    loadTurnstileScript()
      .then(() => {
        if (!widgetRef.current || !window.turnstile) return;

        try {
          widgetRef.current.innerHTML = '';
        } catch {}

        try {
          id = window.turnstile.render(widgetRef.current, {
            sitekey: SITE_KEY,
            theme: 'light',
            size: 'normal',
            callback: (token) => setCfToken(token),
            'expired-callback': () => {
              setCfToken('');
              try {
                if (window.turnstile && id) window.turnstile.reset(id);
              } catch {}
            },
            'error-callback': () => setCfToken(''),
          });

          setWidgetId(id);
        } catch (e) {
          setError(
            t('login.error.widgetRender')
          );
        }
      })
      .catch(() =>
        setError(
          t('login.error.verificationUnavailable')
        )
      );

    return () => {
      if (window.turnstile && id && !removed) {
        try {
          window.turnstile.remove(id);
        } catch {}
        removed = true;
      }
    };
  }, [t]);

  // resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((v) => v - 1), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  function advanceToVerify(softMsg) {
    setStep('verify');
    setResendCooldown(45);
    if (softMsg) setError(softMsg);
  }

  async function handleRequestCode(e) {
    e.preventDefault();
    setError('');

    const cleanEmail = (email || '').trim();

    if (!cleanEmail) return setError(t('login.error.emailRequired'));
    if (!cfToken) return setError(t('login.error.verificationRequired'));

    setSending(true);
    try {
      await ensureCsrf();

      const res = await axios.post(
        'auth/request-otp/',
        { email: cleanEmail, cf_turnstile_response: cfToken },
        { timeout: OTP_REQUEST_TIMEOUT_MS }
      );

      // backend returns 200 even if smtp degraded
      const degraded = Boolean(res?.data?.smtp_degraded);
      advanceToVerify(
        degraded
          ? t('login.warning.emailSlow')
          : ''
      );
    } catch (err) {
      const status = err?.response?.status;

      // If the request timed out or network failed, user may still have received an OTP
      const isTimeout =
        err?.code === 'ECONNABORTED' ||
        String(err?.message || '').toLowerCase().includes('timeout');

      const isNetwork = !err?.response;

      if (isTimeout || isNetwork) {
        advanceToVerify(
          t('login.warning.requestTimedOut')
        );
      } else if (status === 403) {
        setError(
          t('login.error.csrfBlocked')
        );
      } else {
        setError(diagnoseAxiosError(err, t, isZh));
      }

      // Reset turnstile
      try {
        if (window.turnstile && widgetId) window.turnstile.reset(widgetId);
      } catch {}
      setCfToken('');
    } finally {
      setSending(false);
    }
  }

  async function handleVerifyCode(e) {
    e.preventDefault();
    setError('');

    const cleanEmail = (email || '').trim();
    const cleanCode = (code || '').trim();

    if (!cleanEmail || !cleanCode)
      return setError(t('login.error.emailAndCodeRequired'));

    setVerifying(true);
    try {
      await ensureCsrf();

      await axios.post(
        'auth/verify-otp/',
        { email: cleanEmail, code: cleanCode },
        { timeout: OTP_VERIFY_TIMEOUT_MS }
      );

      await sleep(200);

      // Confirm session is established
      await axios.get('auth/whoami/', { timeout: 15_000 });

      try {
        sessionStorage.setItem('justLoggedIn', '1');
        sessionStorage.setItem('whoami_ok_ts', String(Date.now()));
      } catch {}

      navigate('/dashboard/patients', { replace: true });
    } catch (err) {
      const data = err?.response?.data;
      const status = err?.response?.status;

      const serverMsg = data?.non_field_errors?.[0] || data?.detail || data?.code?.[0] || '';
      const msg = (!isZh && serverMsg)
        ? serverMsg
        : `${t('login.error.invalidCode')}${status ? ` (${t('login.diag.httpStatus', { status })})` : ''}`;

      setError(msg);

      if (
        String(msg).toLowerCase().includes('expired') ||
        String(msg).toLowerCase().includes('not found')
      ) {
        setStep('request');
        setCode('');
        setResendCooldown(0);
      }
    } finally {
      setVerifying(false);
    }
  }

  function handleResend() {
    if (resendCooldown > 0) return;
    setStep('request');
    setCode('');
    setError('');
  }

  return (
    <div className="login-container">
      <div className="left-section">
        <img src={contourImage} alt={t('login.contourWelcomeAlt')} className="login-image" />
      </div>

      <div className="right-section">
        <div className="login-card">
          <div className="login-card-top">
            <LanguageSwitcher />
          </div>
          <h1>
            {step === 'request'
              ? t('login.title.request')
              : t('login.title.verify')}
          </h1>
          <p className="login-subtitle">
            {step === 'request'
              ? t('login.subtitle.request')
              : t('login.subtitle.verify')}
          </p>

          {/* Runtime diagnostics: helps when “site loads but page is blank / APIs fail” */}
          {apiStatus !== 'ok' && (
            <div className="error-message" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                {t('login.backendCheck')}: {apiStatus === 'checking' ? t('status.checking') : t('status.failed')}
              </div>
              <div style={{ fontSize: 12, opacity: 0.9 }}>
                {t('login.apiBase')}: <code>{String(API_BASE || '')}</code>
              </div>
              {apiStatus === 'fail' && (
                <div style={{ marginTop: 8, fontSize: 12 }}>
                  {apiDiag || t('login.error.backendUnreachable')}
                </div>
              )}
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          {step === 'request' ? (
            <form onSubmit={handleRequestCode} className="login-form" noValidate>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                required
                className="input-field"
                autoComplete="email"
              />

              <div className="turnstile-box">
                <div ref={widgetRef} className="cf-turnstile" />
              </div>

              <button
                type="submit"
                className="form-button"
                disabled={!cfToken || sending}
              >
                {sending ? t('login.sending') : t('login.sendCode')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} className="login-form" noValidate>
              <input
                type="email"
                id="email-verify"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                required
                className="input-field"
                autoComplete="email"
              />

              <input
                type="text"
                id="otp-code"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                placeholder={t('login.codePlaceholder')}
                required
                className="input-field"
                inputMode="numeric"
                pattern="\d{6}"
                autoComplete="one-time-code"
              />

              <button
                type="submit"
                className="form-button"
                disabled={verifying || code.length !== 6}
              >
                {verifying ? t('login.verifying') : t('login.verifyContinue')}
              </button>

              <div className="resend-row">
                <button
                  type="button"
                  className="link-button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0}
                >
                  {resendCooldown > 0
                    ? t('login.resendIn', { seconds: resendCooldown })
                    : t('login.resendNewCode')}
                </button>
              </div>
            </form>
          )}

          <p style={{ marginTop: 12, marginBottom: 0 }}>
            {t('login.needHelp')}{' '}
            <a href="mailto:it@cranialtech.com" className="signup-link">
              {t('login.contactIT')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
